import { PrimaryButton } from '@/components/buttons';
import { UserTableActions } from '@/components/tables/users-table.actions.components';
import { DataTable } from '@/components/ui/data-table';
import { FilterConfig } from '@/components/ui/filter';
import { useTableUrlState } from '@/hooks/useTableUrlState';
import { cn } from '@/lib/utils';
import { UserGroupNames } from '@/modules/Permissions';
import { routes } from '@/modules/Router';
import { User, useUserListData } from '@/modules/Users/Users.api';
import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const columns: ColumnDef<User, unknown>[] = [
  {
    header: 'Name',
    accessorKey: 'name',
    meta: { className: 'w-1/6' },
    cell: ({ row }) => {
      const name = row.original.name || '-';
      return (
        <span title={name} className="block max-w-[160px] truncate">
          {name}
        </span>
      );
    },
  },
  {
    header: 'Email',
    accessorFn: (user) => user.email || '-',
    enableSorting: false, // Sorting on calculated fields not currently supported
    cell: ({ row }) => {
      const email = row.original.email || '-';
      const truncatedEmail = email.length > 20 ? `${email.slice(0, 20)}...` : email;
      return (
        <a
          href={`mailto:${email}`}
          className="text-blue-600 hover:text-blue-800 hover:underline"
          title={email}
          onClick={(e) => e.stopPropagation()}
        >
          {truncatedEmail}
        </a>
      );
    },
    meta: { className: 'text-left w-48' },
  },
  {
    header: 'Type',
    accessorFn: (user) => user.groups.join(', '),
    enableSorting: false, // Sorting on calculated fields not currently supported
    meta: { className: 'w-1/6' },
  },
  {
    header: 'Customer Access',
    accessorFn: (user) => user.customers.join(', '),
    enableSorting: false, // Sorting on calculated fields not currently supported
    meta: { className: 'w-1/6' },
    cell: ({ row }) => {
      const value = row.original.customers.join(', ') || '-';
      return (
        <span title={value} className="block max-w-[160px] truncate">
          {value}
        </span>
      );
    },
  },
  {
    header: 'Project Access',
    accessorFn: (user) => user.projects.join(', '),
    enableSorting: false, // Sorting on calculated fields not currently supported
    meta: { className: 'w-1/6' },
    cell: ({ row }) => {
      const value = row.original.projects.join(', ') || '-';
      return (
        <span title={value} className="block max-w-[160px] truncate">
          {value}
        </span>
      );
    },
  },
  {
    header: 'Status',
    accessorKey: 'is_active',
    enableSorting: false,
    meta: { className: 'w-[90px]' },
    cell: ({ row }) =>
      row.original.is_active ? (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Active</span>
      ) : (
        <span className="inline-flex items-center rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
          Inactive
        </span>
      ),
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    cell: UserTableActions,
    meta: { className: 'w-[70px]' },
  },
];

export const UsersTable = () => {
  const { pagination, setPagination, sorting, setSorting, columnFilters, setColumnFilters } = useTableUrlState();

  const { data, status } = useUserListData(pagination, sorting, columnFilters);

  if (status === 'error') {
    toast.error('Failed to fetch user data');
    return;
  }

  const filters: FilterConfig[] = [
    {
      id: 'groups',
      label: 'User Type',
      options: Object.values(UserGroupNames).map((value) => ({
        label: value,
        id: value,
      })),
    },
  ];

  const addUserButton = (
    <Link to={routes.addUser}>
      <PrimaryButton size="small" variant="static" className="h-10 px-4">
        Add User
      </PrimaryButton>
    </Link>
  );

  return (
    <div className={cn('p-5 flex-col gap-3')}>
      <DataTable
        columns={columns}
        data={data?.results}
        totalCount={data?.count}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        showSearch={true}
        searchPlaceholder="Search User Email or Name..."
        filters={filters}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        isLoading={status === 'pending'}
        addButton={addUserButton}
      />
    </div>
  );
};
